import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import { fileUrl } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import CityPicker from '../../components/CityPicker';
import {
  Button, Chip, Empty, Input, Loading, Rating,
} from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { useApp } from '../../context/AppContext';
import { currency } from '../../utils/format';
import {
  HOTEL_AMENITIES, NIGHT_PRICES, ROOM_TYPES, STAY_RATINGS,
} from '../../constants';

const iso = (d) => new Date(d).toISOString().slice(0, 10);
const addDays = (date, n) => new Date(new Date(date).getTime() + n * 86400000);
const amenityLabel = (a) => (HOTEL_AMENITIES.find((x) => x.value === a) || {}).label || a;

const HotelCard = ({ hotel, nights, onClick }) => (
  <button type="button" className="card sh-sm hotel-card" onClick={onClick}>
    {(hotel.photos || []).length ? (
      <img src={fileUrl(hotel.photos[0])} alt={hotel.name} className="hotel-cover" />
    ) : (
      <div className="hotel-cover hotel-cover-fallback">🏨</div>
    )}
    <div style={{ padding: 12, textAlign: 'left' }}>
      <div className="row-between">
        <div className="t-title clamp-1 grow">{hotel.name}</div>
        <Rating value={hotel.rating} count={hotel.ratingCount} />
      </div>
      <div className="t-small clamp-1" style={{ marginTop: 2 }}>
        {[hotel.address && hotel.address.line1, hotel.address && hotel.address.city].filter(Boolean).join(', ')}
      </div>
      <div className="row wrap" style={{ gap: 6, marginTop: 8 }}>
        {hotel.distanceKm !== undefined ? <span className="pill">📍 {hotel.distanceKm} km</span> : null}
        {(hotel.amenities || []).slice(0, 3).map((a) => <span key={a} className="pill">{amenityLabel(a)}</span>)}
      </div>
      <div className="row-between" style={{ marginTop: 10 }}>
        <div>
          <span className="t-body" style={{ fontWeight: 800 }}>{currency(hotel.fromPrice)}</span>
          <span className="t-tiny"> / night</span>
          {nights > 1 ? <div className="t-tiny">{currency(hotel.fromPrice * nights)} for {nights} nights</div> : null}
        </div>
        <span className="t-tiny" style={{ color: hotel.hasAvailability ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>
          {hotel.hasAvailability ? 'Rooms available' : 'Fully booked'}
        </span>
      </div>
    </div>
  </button>
);

/** Find a stay: where, which nights, how many guests, and what it costs. */
const HotelsPage = () => {
  const navigate = useNavigate();
  const { location } = useApp();

  const [hotels, setHotels] = useState([]);
  const [search, setSearch] = useState('');
  const [place, setPlace] = useState({ city: '', district: '', state: '' });
  const [checkIn, setCheckIn] = useState(iso(addDays(new Date(), 1)));
  const [checkOut, setCheckOut] = useState(iso(addDays(new Date(), 2)));
  const [guests, setGuests] = useState(2);
  const [maxPrice, setMaxPrice] = useState('');
  const [rating, setRating] = useState('');
  const [roomType, setRoomType] = useState('');
  const [ac, setAc] = useState('');
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);

  const nights = Math.max(1, Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000));

  const load = useCallback(async () => {
    try {
      const res = await api.hotels.list({
        search: search || undefined,
        city: place.city || undefined,
        lat: place.city ? undefined : location.latitude,
        lng: place.city ? undefined : location.longitude,
        radius: 30,
        checkIn,
        checkOut,
        guests,
        maxPrice: maxPrice || undefined,
        rating: rating || undefined,
        roomType: roomType || undefined,
        ac: ac === '' ? undefined : ac,
        amenities: amenities.length ? amenities.join(',') : undefined,
      });
      setHotels(res.data || []);
    } catch (e) {
      setHotels([]);
    } finally {
      setLoading(false);
    }
  }, [search, place.city, location.latitude, location.longitude, checkIn, checkOut, guests, maxPrice, rating, roomType, ac, amenities]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const toggleAmenity = (value) => setAmenities((list) => (
    list.includes(value) ? list.filter((a) => a !== value) : [...list, value]
  ));

  // Check-out always stays at least one night after check-in.
  const pickCheckIn = (value) => {
    setCheckIn(value);
    if (new Date(checkOut) <= new Date(value)) setCheckOut(iso(addDays(value, 1)));
  };

  return (
    <div>
      <PageHeader
        title="Hotels"
        subtitle={`${hotels.length} stays ${place.city ? `in ${place.city}` : `near ${location.city}`}`}
        right={<Button title="My stays" variant="light" size="sm" onClick={() => navigate('/my-stays')} />}
      />

      <div className="card sh-sm" style={{ marginBottom: 16 }}>
        <Input
          icon="🔍"
          placeholder="Hotel name or area"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: 12 }}
        />
        <div className="split-2" style={{ gap: 12 }}>
          <Input label="Check-in" type="date" value={checkIn} min={iso(new Date())} onChange={(e) => pickCheckIn(e.target.value)} />
          <Input label="Check-out" type="date" value={checkOut} min={iso(addDays(checkIn, 1))} onChange={(e) => setCheckOut(e.target.value)} />
        </div>
        <div className="split-2" style={{ gap: 12 }}>
          <CityPicker label="City" value={place} onChange={({ city, district, state }) => setPlace({ city, district, state })} />
          <div>
            <label className="field-label">Guests</label>
            <div className="row wrap" style={{ gap: 8 }}>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <Chip key={n} label={`${n}`} active={guests === n} onClick={() => setGuests(n)} />
              ))}
            </div>
          </div>
        </div>

        <label className="field-label" style={{ marginTop: 12 }}>Price per night</label>
        <div className="row wrap" style={{ gap: 8 }}>
          {NIGHT_PRICES.map((p) => (
            <Chip key={p.value || 'any'} label={p.label} active={maxPrice === p.value} onClick={() => setMaxPrice(p.value)} />
          ))}
        </div>

        <label className="field-label" style={{ marginTop: 12 }}>Rating</label>
        <div className="row wrap" style={{ gap: 8 }}>
          {STAY_RATINGS.map((r) => (
            <Chip key={r.value || 'any'} label={r.label} active={rating === r.value} onClick={() => setRating(r.value)} />
          ))}
        </div>

        <label className="field-label" style={{ marginTop: 12 }}>Room type</label>
        <div className="row wrap" style={{ gap: 8 }}>
          <Chip label="Any room" active={!roomType} onClick={() => setRoomType('')} />
          {ROOM_TYPES.map((r) => (
            <Chip key={r.value} label={r.label} active={roomType === r.value} onClick={() => setRoomType(r.value)} />
          ))}
          <Chip label="Any AC" active={ac === ''} onClick={() => setAc('')} />
          <Chip label="❄️ AC" active={ac === 'true'} onClick={() => setAc('true')} />
          <Chip label="Non-AC" active={ac === 'false'} onClick={() => setAc('false')} />
        </div>

        <label className="field-label" style={{ marginTop: 12 }}>Amenities</label>
        <div className="row wrap" style={{ gap: 8 }}>
          {HOTEL_AMENITIES.map((a) => (
            <Chip key={a.value} label={a.label} active={amenities.includes(a.value)} onClick={() => toggleAmenity(a.value)} />
          ))}
        </div>
      </div>

      {loading ? <Loading /> : !hotels.length ? (
        <Empty emoji="🏨" title="No stays found" text="Try other dates, a wider price range or a different city." />
      ) : (
        <div className="grid grid-cards">
          {hotels.map((hotel, i) => (
            <Reveal key={hotel._id} delay={stagger(i, 45)}>
              <HotelCard
                hotel={hotel}
                nights={nights}
                onClick={() => navigate(`/hotels/${hotel._id}?checkIn=${checkIn}&checkOut=${checkOut}&guests=${guests}`)}
              />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
};

export default HotelsPage;
